import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
  Tabs,
  Switch,
} from 'antd';
import { PlusOutlined, ReloadOutlined, CheckOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  enterpriseMemoryService,
  type MemoryCandidate,
  type EnterpriseMemoryRow,
  type RetrievedMemoryRow,
} from '@/services/enterpriseMemory.service';
import { MemoryReasonTags } from '@/components/ai-agent/MemoryReasonTags';
import { useAuthStore } from '@/store/authStore';

const { Title, Text, Paragraph } = Typography;

const MEMORY_TYPES = [
  'lesson_learned',
  'quality',
  'supplier',
  'customer',
  'contract',
  'manufacturing',
  'risk',
  'mitigation',
  'decision',
  'outcome',
  'process',
  'financial_control',
  'policy_governance',
];

export default function EnterpriseMemoryPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [tab, setTab] = useState('candidates');
  const [createOpen, setCreateOpen] = useState(false);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [retrieveKeyword, setRetrieveKeyword] = useState('');
  const [retrieveHybrid, setRetrieveHybrid] = useState(true);
  const [retrieveResults, setRetrieveResults] = useState<RetrievedMemoryRow[]>([]);
  const [retrieveLoading, setRetrieveLoading] = useState(false);
  const [form] = Form.useForm();
  const [promoteForm] = Form.useForm();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedSubjectLabel = Form.useWatch('subjectLabel', form) as string | undefined;

  // Help Center "Add company note": /ai/enterprise-memory?newCandidate=1&memoryType=process&subjectType=how_to_guide&subjectId=<guideId>…
  useEffect(() => {
    if (searchParams.get('newCandidate') !== '1') return;
    const subjectId = searchParams.get('subjectId') || undefined;
    form.setFieldsValue({
      title: searchParams.get('title') || undefined,
      memoryType: searchParams.get('memoryType') || 'process',
      domain: searchParams.get('domain') || undefined,
      subjectType: searchParams.get('subjectType') || undefined,
      subjectId,
      subjectLabel: searchParams.get('subjectLabel') || undefined,
      sourceType: searchParams.get('sourceType') || 'HowToGuide',
      sourceId: subjectId,
    });
    setCreateOpen(true);
    const next = new URLSearchParams(searchParams);
    ['newCandidate', 'title', 'memoryType', 'domain', 'subjectType', 'subjectId', 'subjectLabel', 'sourceType'].forEach((k) =>
      next.delete(k),
    );
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, form]);

  const candidatesQuery = useQuery({
    queryKey: ['enterprise-memory-candidates'],
    queryFn: () => enterpriseMemoryService.listCandidates({ limit: 100 }),
  });

  const memoriesQuery = useQuery({
    queryKey: ['enterprise-memory-list'],
    queryFn: () => enterpriseMemoryService.listMemories({ limit: 100 }),
  });

  const healthQuery = useQuery({
    queryKey: ['enterprise-memory-health'],
    queryFn: () => enterpriseMemoryService.health(),
  });

  const reviewQuery = useQuery({
    queryKey: ['enterprise-memory-candidate', reviewId],
    queryFn: () => enterpriseMemoryService.getCandidate(reviewId!),
    enabled: Boolean(reviewId),
  });

  const createMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      enterpriseMemoryService.createCandidate({
        ...values,
        evidence: values.sourceId
          ? [
              {
                sourceType: values.sourceType || 'Manual',
                sourceId: values.sourceId,
                documentNumber: values.sourceNumber || null,
                excerpt: values.proposedSummary,
              },
            ]
          : undefined,
      }),
    onSuccess: () => {
      message.success('Candidate created');
      setCreateOpen(false);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['enterprise-memory-candidates'] });
      qc.invalidateQueries({ queryKey: ['enterprise-memory-health'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const rejectMut = useMutation({
    mutationFn: (id: string) =>
      enterpriseMemoryService.patchCandidate(id, { status: 'REJECTED', rejectionReason: 'Rejected in review UI' }),
    onSuccess: () => {
      message.success('Candidate rejected');
      setReviewId(null);
      qc.invalidateQueries({ queryKey: ['enterprise-memory-candidates'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const promoteMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      enterpriseMemoryService.promoteCandidate(reviewId!, {
        ownership: {
          domainOwnerId: values.domainOwnerId || user?.id,
          businessOwnerId: values.businessOwnerId || user?.id,
        },
        applicability: {
          domains: values.domain ? [String(values.domain)] : ['quality'],
          tenantWide: false,
        },
        validity: {
          effectiveFrom: new Date().toISOString().slice(0, 10),
          reviewDueAt: values.reviewDueAt || undefined,
        },
        tags: String(values.tags || '')
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      }),
    onSuccess: (mem) => {
      message.success('Promoted to under-review memory');
      setReviewId(null);
      qc.invalidateQueries({ queryKey: ['enterprise-memory-candidates'] });
      qc.invalidateQueries({ queryKey: ['enterprise-memory-list'] });
      navigate(`/ai/enterprise-memory/${(mem as { id?: string }).id || ''}`);
    },
    onError: (e: Error) => message.error(e.message),
  });

  const publishMut = useMutation({
    mutationFn: (id: string) => enterpriseMemoryService.publish(id),
    onSuccess: () => {
      message.success('Memory published (ACTIVE)');
      qc.invalidateQueries({ queryKey: ['enterprise-memory-list'] });
      qc.invalidateQueries({ queryKey: ['enterprise-memory-health'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const candidateColumns = useMemo(
    () => [
      { title: 'Title', dataIndex: 'title', key: 'title', ellipsis: true },
      { title: 'Type', dataIndex: 'memoryType', key: 'memoryType', width: 120 },
      { title: 'Domain', dataIndex: 'domain', key: 'domain', width: 110 },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 130,
        render: (s: string) => <Tag>{s}</Tag>,
      },
      { title: 'Source', dataIndex: 'sourceEventNumber', key: 'source', width: 120 },
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        render: (_: unknown, row: MemoryCandidate) => (
          <Button size="small" type="link" onClick={() => setReviewId(row.id)} disabled={row.status === 'PROMOTED'}>
            Review
          </Button>
        ),
      },
    ],
    []
  );

  const memoryColumns = useMemo(
    () => [
      {
        title: 'Title',
        dataIndex: 'title',
        key: 'title',
        ellipsis: true,
        render: (t: string, row: EnterpriseMemoryRow) => (
          <Button type="link" onClick={() => navigate(`/ai/enterprise-memory/${row.id}`)}>
            {t}
          </Button>
        ),
      },
      { title: 'Domain', dataIndex: 'domain', key: 'domain', width: 110 },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (s: string) => <Tag color={s === 'ACTIVE' || s === 'PUBLISHED' ? 'green' : 'default'}>{s}</Tag>,
      },
      {
        title: 'Trust',
        dataIndex: 'trustScore',
        key: 'trust',
        width: 80,
        render: (v: number) => Number(v).toFixed(0),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 140,
        render: (_: unknown, row: EnterpriseMemoryRow) =>
          row.status === 'UNDER_REVIEW' || row.status === 'VALIDATED' ? (
            <Button size="small" type="primary" onClick={() => publishMut.mutate(row.id)}>
              Publish
            </Button>
          ) : null,
      },
    ],
    [navigate, publishMut]
  );

  const runRetrieve = async () => {
    const kw = retrieveKeyword.trim();
    if (!kw) {
      message.warning('Enter a keyword to retrieve');
      return;
    }
    setRetrieveLoading(true);
    try {
      const rows = await enterpriseMemoryService.retrieve({
        keyword: kw,
        hybrid: retrieveHybrid,
        limit: 15,
      });
      setRetrieveResults(rows);
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Retrieve failed');
    } finally {
      setRetrieveLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} align="start">
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Enterprise Memory
          </Title>
          <Text type="secondary">Governed organizational knowledge — candidates require validation before publication.</Text>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              candidatesQuery.refetch();
              memoriesQuery.refetch();
              healthQuery.refetch();
            }}
          >
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            New candidate
          </Button>
        </Space>
      </Space>

      <Space wrap style={{ marginBottom: 16 }}>
        <Card size="small">
          <Text type="secondary">Candidate backlog</Text>
          <div style={{ fontSize: 20, fontWeight: 600 }}>{Number(healthQuery.data?.candidate_backlog || 0)}</div>
        </Card>
        <Card size="small">
          <Text type="secondary">Active / published</Text>
          <div style={{ fontSize: 20, fontWeight: 600 }}>
            {Number((healthQuery.data?.by_status as Record<string, number> | undefined)?.ACTIVE || 0) +
              Number((healthQuery.data?.by_status as Record<string, number> | undefined)?.PUBLISHED || 0)}
          </div>
        </Card>
        <Card size="small">
          <Text type="secondary">Review due (30d)</Text>
          <div style={{ fontSize: 20, fontWeight: 600 }}>{Number(healthQuery.data?.review_due_30d || 0)}</div>
        </Card>
      </Space>

      <Card>
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {
              key: 'candidates',
              label: 'Candidates',
              children: (
                <Table
                  rowKey="id"
                  loading={candidatesQuery.isLoading}
                  dataSource={candidatesQuery.data || []}
                  columns={candidateColumns}
                  pagination={{ pageSize: 20 }}
                />
              ),
            },
            {
              key: 'published',
              label: 'Memories',
              children: (
                <Table
                  rowKey="id"
                  loading={memoriesQuery.isLoading}
                  dataSource={memoriesQuery.data || []}
                  columns={memoryColumns}
                  pagination={{ pageSize: 20 }}
                />
              ),
            },
            {
              key: 'retrieve',
              label: 'Retrieve',
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Space wrap align="center">
                    <Input
                      placeholder="Keyword or question fragment"
                      value={retrieveKeyword}
                      onChange={(e) => setRetrieveKeyword(e.target.value)}
                      onPressEnter={() => void runRetrieve()}
                      style={{ width: 320 }}
                    />
                    <span>
                      Hybrid semantic <Switch checked={retrieveHybrid} onChange={setRetrieveHybrid} size="small" />
                    </span>
                    <Button type="primary" icon={<SearchOutlined />} loading={retrieveLoading} onClick={() => void runRetrieve()}>
                      Retrieve
                    </Button>
                  </Space>
                  {retrieveResults.map((row) => (
                    <Card key={row.memory.id} size="small">
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/ai/enterprise-memory/${row.memory.id}`)}>
                          {row.memory.title}
                        </Button>
                        <Text type="secondary">{row.memory.summary?.slice(0, 200)}</Text>
                        <MemoryReasonTags
                          items={[
                            {
                              id: row.memory.id,
                              title: row.memory.title,
                              reasons: row.reasons?.length ? row.reasons : ['Matched published memory'],
                            },
                          ]}
                        />
                      </Space>
                    </Card>
                  ))}
                  {!retrieveLoading && retrieveResults.length === 0 && retrieveKeyword.trim() ? (
                    <Text type="secondary">No matches — try hybrid mode or different keywords.</Text>
                  ) : null}
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Drawer title="Create memory candidate" open={createOpen} onClose={() => setCreateOpen(false)} width={480} forceRender>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ memoryType: 'lesson_learned', domain: 'quality', sourceType: 'Manual' }}
          onFinish={(v) => createMut.mutate(v)}
        >
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="memoryType" label="Memory type" rules={[{ required: true }]}>
            <Select options={MEMORY_TYPES.map((t) => ({ value: t, label: t }))} />
          </Form.Item>
          <Form.Item name="domain" label="Domain" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="proposedSummary" label="Proposed lesson" rules={[{ required: true }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name="proposedDetail" label="Detail">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="sourceType" label="Evidence source type">
            <Input />
          </Form.Item>
          <Form.Item name="sourceId" label="Evidence source ID" rules={[{ required: true, message: 'Evidence source required' }]}>
            <Input placeholder="ERP entity UUID or stable id" />
          </Form.Item>
          <Form.Item name="sourceNumber" label="Document number">
            <Input />
          </Form.Item>
          <Form.Item name="subjectType" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="subjectId" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="subjectLabel" label="Linked to" hidden={!linkedSubjectLabel}>
            <Input disabled />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={createMut.isPending} block>
            Create candidate
          </Button>
        </Form>
      </Drawer>

      <Drawer
        title="Review candidate"
        open={Boolean(reviewId)}
        onClose={() => setReviewId(null)}
        width={560}
        extra={
          <Space>
            <Button
              danger
              icon={<CloseOutlined />}
              loading={rejectMut.isPending}
              onClick={() => reviewId && rejectMut.mutate(reviewId)}
            >
              Reject
            </Button>
          </Space>
        }
      >
        {reviewQuery.data && (
          <>
            <Title level={5}>{reviewQuery.data.title}</Title>
            <Paragraph>{reviewQuery.data.proposedSummary}</Paragraph>
            {reviewQuery.data.proposedDetail && (
              <Paragraph type="secondary">{reviewQuery.data.proposedDetail}</Paragraph>
            )}
            <Text type="secondary">
              {reviewQuery.data.memoryType} · {reviewQuery.data.domain} · {reviewQuery.data.status}
            </Text>
            <div style={{ marginTop: 16 }}>
              <Text strong>Evidence ({reviewQuery.data.evidence?.length || 0})</Text>
              <ul>
                {(reviewQuery.data.evidence || []).map((e: any) => (
                  <li key={e.id}>
                    {e.source_type || e.sourceType}: {e.document_number || e.source_id || e.sourceId}
                  </li>
                ))}
              </ul>
            </div>
            <Form
              form={promoteForm}
              layout="vertical"
              style={{ marginTop: 24 }}
              initialValues={{
                domain: reviewQuery.data.domain,
                domainOwnerId: user?.id,
                tags:
                  reviewQuery.data.subjectType === 'how_to_guide' && reviewQuery.data.subjectId
                    ? `guide:${reviewQuery.data.subjectId}`
                    : undefined,
              }}
              onFinish={(v) => promoteMut.mutate(v)}
            >
              <Form.Item name="domain" label="Applicability domain" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="domainOwnerId" label="Domain owner user id" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="tags" label="Tags (comma-separated)">
                <Input />
              </Form.Item>
              <Button type="primary" htmlType="submit" icon={<CheckOutlined />} loading={promoteMut.isPending} block>
                Promote to under-review memory
              </Button>
            </Form>
          </>
        )}
      </Drawer>
    </div>
  );
}
